<template>
  <v-container fluid v-if="!isUnknown" style="position: relative">
    <div :style="style" ref="modelContainer" />
    <v-progress-circular
      v-if="!loaded"
      indeterminate
      :size="heightInt / 2"
      style="
        position: absolute;
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
      "
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import { fetchAndLoadModel } from "./ModelViewer/model_viewer_utils"
import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"

export default {
  props: ["filename", "url", "height", "width", "exportAbutment"],

  watch: {
    url(newValue, previousValue) {
      if (newValue === previousValue) {
        return
      }
      this.loaded = false
      this.cleanup()
      this.initLoad()
    }
  },

  data() {
    return {
      loaded: false,
      mesh: null,
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      reqId: null,
      abortController: null
    }
  },

  computed: {
    ...mapGetters(["modelViewerCache", "getModelBufferCache"]),

    style() {
      return {
        height: `${this.heightInt}px`,
        width: `${this.widthInt}px`
      }
    },

    heightInt() {
      return parseInt(this.height)
    },

    widthInt() {
      return parseInt(this.width)
    },

    isSTLFile() {
      if (!this.filename) {
        return false
      }
      if (this.filename.toLowerCase().includes(".stl")) {
        return true
      }
      return false
    },

    isPLYFile() {
      if (!this.filename) {
        return false
      }
      if (this.filename.toLowerCase().includes(".ply")) {
        return true
      }
      return false
    },

    isUnknown() {
      return !this.isPLYFile && !this.isSTLFile
    }
  },

  methods: {
    setLoaded() {
      this.loaded = true
    },

    animate() {
      this.reqId = requestAnimationFrame(this.animate.bind(this))
      this.controls.update()
      this.renderer.render(this.scene, this.camera)
    },

    async initLoad() {
      if (this.isUnknown) return

      // For cleanup mid load
      this.abortController = new AbortController()
      const signal = this.abortController.signal

      try {
        const modelBuffer = this.getModelBufferCache(this.filename)
        this.mesh = await fetchAndLoadModel(
          this.filename,
          this.url,
          modelBuffer,
          this.isSTLFile ? "stl" : "ply",
          signal
        )
        if (signal.aborted) return

        // Scene setup
        this.scene = new THREE.Scene()

        // Camera setup
        this.camera = new THREE.PerspectiveCamera(
          50,
          this.widthInt / this.heightInt,
          0.1,
          1000
        )

        // Renderer setup
        // TODO: Reuse a single renderer for multiple models
        this.renderer = new THREE.WebGLRenderer({ antialias: true })
        this.renderer.shadowMap.enabled = false
        this.renderer.setSize(this.widthInt, this.heightInt)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
        if (this.$refs.modelContainer)
          this.$refs.modelContainer.appendChild(this.renderer.domElement)
        this.renderer.setClearColor(0xffffff, 1)

        // Controls setup
        this.controls = new OrbitControls(this.camera, this.renderer.domElement)
        this.controls.enableDamping = false
        this.controls.autoRotate = false
        this.controls.enableZoom = true
        this.controls.minDistance = 10
        this.controls.maxDistance = 100

        // Lighting
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.0)
        keyLight.position.set(1, 1, 1).normalize()
        this.scene.add(keyLight)

        const fillLight = new THREE.DirectionalLight(0xffffff, 0.6)
        fillLight.position.set(-1, -1, -1).normalize()
        this.scene.add(fillLight)

        const ambient = new THREE.AmbientLight(0xffffff, 0.3)
        this.scene.add(ambient)

        // Add mesh
        if (this.mesh) {
          this.scene.add(this.mesh)
          const box = new THREE.Box3().setFromObject(this.mesh)
          const size = box.getSize(new THREE.Vector3())
          const center = box.getCenter(new THREE.Vector3())

          this.controls.target.copy(center)
          this.camera.position
            .copy(center)
            .add(new THREE.Vector3(0, 0, size.length() * 0.75))
          this.camera.lookAt(center)

          if (this.exportAbutment) {
            this.mesh.rotation.set(0, Math.PI / 6, 0)
            this.camera.position.set(0, 0, 25)
          }

          this.controls.update()
        }

        this.setLoaded()

        this.animate()
      } catch (error) {
        if (error.name === "AbortError") {
          return
        } else {
          console.error("Error during model load:", error)
        }
      } finally {
        this.abortController = null
      }
    },

    cleanup() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      if (this.reqId) {
        cancelAnimationFrame(this.reqId)
        this.reqId = null
      }

      if (this.mesh) {
        this.scene?.remove(this.mesh)

        if (this.mesh.geometry) {
          this.mesh.geometry.dispose()
        }

        if (this.mesh.material) {
          if (Array.isArray(this.mesh.material)) {
            this.mesh.material.forEach((m) => m.dispose?.())
          } else {
            this.mesh.material.dispose?.()
          }
        }
        this.mesh = null
      }

      this.controls?.dispose?.()
      this.controls = null

      if (this.renderer) {
        if (this.renderer.getContext) {
          const gl = this.renderer.getContext()
          const loseContext = gl.getExtension("WEBGL_lose_context")
          if (loseContext) loseContext.loseContext()
        }
        this.renderer.dispose()

        const canvas = this.renderer.domElement
        if (canvas?.parentNode) {
          canvas.parentNode.removeChild(canvas)
        }
        this.renderer = null
      }

      this.scene = null
      this.camera = null
    }
  },

  async mounted() {
    await this.initLoad()
  },

  beforeDestroy() {
    this.cleanup()
  }
}
</script>
